<template>
  <div class="request-stations-container">
    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <v-progress-circular
        indeterminate
        color="primary"
        size="48"
      ></v-progress-circular>
      <p class="mt-2">جاري التحميل...</p>
    </div>

    <!-- Empty State -->
    <div v-else-if="!users || users.length === 0" class="empty-state">
      <v-icon size="48" color="grey lighten-1">mdi-history</v-icon>
      <p class="mt-2">لا توجد تغييرات مسجلة</p>
    </div>

    <!-- Users Timeline -->
    <div v-else class="users-timeline">
      <div 
        v-for="(user, userIndex) in users" 
        :key="getUserKey(user, userIndex)"
        class="user-section"
        :class="{ 'expanded': expandedUsers.includes(getUserKey(user, userIndex)) }"
      >
        <!-- User Header -->
        <div 
          class="user-header"
          @click="toggleUser(getUserKey(user, userIndex))"
        >
          <div class="user-avatar">
            <span>{{ getUserInitials(user.user_name) }}</span>
          </div>
          <div class="user-info">
            <div class="user-name">{{ user.user_name || 'النظام' }}</div>
            <div class="user-email" v-if="user.user_email">{{ user.user_email }}</div>
          </div>
          <div class="user-stats">
            <span class="badge">{{ user.total_changes }}</span>
          </div>
          <v-icon class="expand-icon" size="20">
            {{ expandedUsers.includes(getUserKey(user, userIndex)) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
          </v-icon>
        </div>

        <!-- User Changes Timeline -->
        <transition name="slide-fade">
          <div v-if="expandedUsers.includes(getUserKey(user, userIndex))" class="changes-timeline">
            <div 
              v-for="(change, changeIndex) in user.changes" 
              :key="change.station_id || changeIndex"
              class="timeline-item"
            >
              <!-- Timeline Line -->
              <div class="timeline-line" v-if="changeIndex < user.changes.length - 1"></div>
              
              <!-- Timeline Dot -->
              <div class="timeline-dot" :class="getEventTypeClass(change.event_type)">
                <v-icon x-small :color="getEventTypeColor(change.event_type)">
                  {{ getEventTypeIcon(change.event_type) }}
                </v-icon>
              </div>

              <!-- Event Card -->
              <div class="event-card" :class="getEventTypeClass(change.event_type)">
                <div class="event-header">
                  <div class="event-time-badge">
                    <v-icon x-small class="mr-1">mdi-clock-outline</v-icon>
                    <span>{{ formatDate(change.at) }}</span>
                  </div>
                  <span class="event-type-badge" :class="getEventTypeClass(change.event_type)">
                    {{ getEventTypeLabel(change.event_type) }}
                  </span>
                </div>
                
                <!-- Description (Most Important) -->
                <div class="event-description">
                  {{ change.description || 'لا يوجد وصف' }}
                </div>

                <!-- Additional Info -->
                <div class="event-details" v-if="change.step_label || change.statusReq">
                  <span v-if="change.step_label" class="step-badge">
                    <v-icon x-small>mdi-tag</v-icon>
                    {{ change.step_label }}
                  </span>
                  <span v-if="change.statusReq" class="status-badge" :class="getStatusClass(change.statusReq)">
                    {{ getStatusLabel(change.statusReq) }}
                  </span>
                </div>

                <!-- Changes Details (if available) -->
                <div v-if="change.changes && change.changes.length > 0" class="changes-details">
                  <div 
                    v-for="(detail, detailIndex) in change.changes" 
                    :key="detailIndex"
                    class="change-item"
                  >
                    <span class="change-field">{{ detail.field || 'حقل' }}:</span>
                    <span class="change-from">{{ detail.from || 'N/A' }}</span>
                    <v-icon x-small class="change-arrow">mdi-arrow-left</v-icon>
                    <span class="change-to">{{ detail.to || 'N/A' }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </transition>
      </div>
    </div>
  </div>
</template>

<script>
import Axios from "axios";
import { base } from "@/mixins/base";

export default {
  name: 'RequestStationsByUser',
  mixins: [base],
  props: {
    requestId: {
      type: [Number, String],
      required: true
    }
  },
  data() {
    return {
      loading: false,
      users: [],
      expandedUsers: [],
      eventTypes: {
        1: { label: 'إنشاء', icon: 'mdi-plus', class: 'event-created', color: '#3b82f6' },
        2: { label: 'تعيين/نقل', icon: 'mdi-arrow-right', class: 'event-assigned', color: '#10b981' },
        3: { label: 'إلغاء التعيين', icon: 'mdi-arrow-left', class: 'event-unassigned', color: '#94a3b8' },
        4: { label: 'تغيير الحالة', icon: 'mdi-refresh', class: 'event-status', color: '#3b82f6' },
        5: { label: 'تغيير التصنيف', icon: 'mdi-tag', class: 'event-step', color: '#8b5cf6' },
        6: { label: 'إرسال', icon: 'mdi-send', class: 'event-sent', color: '#f59e0b' }
      }
    }
  },
  mounted() {
    this.fetchStations()
  },
  methods: {
    async fetchStations() {
      this.loading = true
      try {
        const response = await Axios.get(
          `${this.$store.state.url}/v2/employee/Request/Stations/ByUser/${this.requestId}`,
          { headers: this.request_headers() }
        )
        if (response.data && response.data.payload) {
          this.users = response.data.payload.users || []
        }
      } catch (error) {
        console.error('Error fetching stations:', error)
        this.showPopup("error", error.response?.data?.message || 'حدث خطأ في تحميل التغييرات')
      } finally {
        this.loading = false
      }
    },
    getUserKey(user, index) {
      return user.user_id || `system_${index}`
    },
    toggleUser(userKey) {
      const index = this.expandedUsers.indexOf(userKey)
      if (index > -1) {
        this.expandedUsers.splice(index, 1)
      } else {
        this.expandedUsers.push(userKey)
      }
    },
    getUserInitials(name) {
      if (!name) return '?'
      const parts = name.split(' ')
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase()
      }
      return name.substring(0, 2).toUpperCase()
    },
    formatDate(dateString) {
      if (!dateString) return ''
      try {
        const date = new Date(dateString)
        const options = { 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        }
        return new Intl.DateTimeFormat('ar-SA', options).format(date)
      } catch (e) {
        return dateString
      }
    },
    getEventTypeClass(type) {
      return this.eventTypes[type]?.class || 'event-default'
    },
    getEventTypeIcon(type) {
      return this.eventTypes[type]?.icon || 'mdi-circle'
    },
    getEventTypeColor(type) {
      return this.eventTypes[type]?.color || '#64748b'
    },
    getEventTypeLabel(type) {
      return this.eventTypes[type]?.label || 'غير معروف'
    },
    getStatusClass(status) {
      const statusClasses = {
        0: 'status-new',
        1: 'status-opened',
        15: 'status-cancelled',
        16: 'status-completed',
        27: 'status-cancelled',
        34: 'status-cancelled',
        35: 'status-completed'
      }
      return statusClasses[status] || 'status-default'
    },
    getStatusLabel(status) {
      const statusLabels = {
        0: 'طلب جديد',
        1: 'طلب تم فتحه',
        15: 'ملغي',
        16: 'مكتمل',
        27: 'ملغي',
        34: 'ملغي',
        35: 'مكتمل'
      }
      return statusLabels[status] || `الحالة ${status}`
    }
  }
}
</script>

<style scoped>
.request-stations-container {
  direction: rtl;
  font-family: 'Cairo', 'Segoe UI', Tahoma, sans-serif;
  padding: 12px;
  background: #f8fafc;
  min-height: 100px;
  max-height: 80vh;
  overflow-y: auto;
}

/* Loading & Empty States */
.loading-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 150px;
  color: #64748b;
  font-size: 13px;
}

.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #64748b;
  font-size: 13px;
}

/* User Section */
.user-section {
  background: white;
  border-radius: 8px;
  margin-bottom: 12px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: all 0.2s ease;
  border: 1px solid #e2e8f0;
}

.user-section:hover {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.12);
}

.user-header {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  cursor: pointer;
  transition: background 0.2s;
}

.user-header:hover {
  background: #f8fafc;
}

.user-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 13px;
  margin-left: 10px;
  flex-shrink: 0;
}

.user-info {
  flex: 1;
  min-width: 0;
}

.user-name {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-email {
  font-size: 11px;
  color: #64748b;
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-stats {
  margin-left: 8px;
}

.badge {
  display: inline-block;
  padding: 3px 8px;
  background: #e0e7ff;
  color: #4338ca;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  min-width: 24px;
  text-align: center;
}

.expand-icon {
  margin-right: 8px;
  color: #64748b;
  transition: transform 0.2s;
  flex-shrink: 0;
}

.user-section.expanded .expand-icon {
  transform: rotate(180deg);
}

/* Timeline */
.changes-timeline {
  padding: 10px 12px;
  position: relative;
}

.timeline-item {
  position: relative;
  padding-right: 28px;
  margin-bottom: 12px;
}

.timeline-item:last-child {
  margin-bottom: 0;
}

.timeline-line {
  position: absolute;
  right: 13px;
  top: 28px;
  bottom: -12px;
  width: 2px;
  background: #e2e8f0;
}

.timeline-dot {
  position: absolute;
  right: 4px;
  top: 4px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  border: 2px solid;
  z-index: 1;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

/* Event Card */
.event-card {
  background: white;
  border-radius: 6px;
  padding: 10px 12px;
  border-right: 3px solid;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  transition: all 0.15s;
}

.event-card:hover {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transform: translateX(-1px);
}

.event-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  flex-wrap: wrap;
  gap: 6px;
}

.event-time-badge {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  color: #64748b;
}

.event-type-badge {
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  white-space: nowrap;
}

.event-description {
  margin: 8px 0;
  padding: 8px 10px;
  background: #f1f5f9;
  border-radius: 4px;
  border-right: 2px solid #3b82f6;
  font-size: 13px;
  font-weight: 500;
  color: #1e293b;
  line-height: 1.5;
}

.event-details {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 8px;
}

.step-badge,
.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 6px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 500;
}

.step-badge {
  background: #ede9fe;
  color: #7c3aed;
}

.status-badge {
  background: #dbeafe;
  color: #1e40af;
}

.changes-details {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #e2e8f0;
}

.change-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #475569;
  margin-bottom: 4px;
}

.change-item:last-child {
  margin-bottom: 0;
}

.change-field {
  font-weight: 600;
  color: #334155;
  font-size: 11px;
}

.change-from,
.change-to {
  padding: 1px 6px;
  border-radius: 3px;
  background: #f1f5f9;
  font-size: 11px;
}

.change-arrow {
  margin: 0 2px;
}

/* Event Type Classes */
.event-created {
  border-color: #3b82f6;
}

.event-created .timeline-dot {
  border-color: #3b82f6;
}

.event-assigned {
  border-color: #10b981;
}

.event-assigned .timeline-dot {
  border-color: #10b981;
}

.event-unassigned {
  border-color: #94a3b8;
}

.event-unassigned .timeline-dot {
  border-color: #94a3b8;
}

.event-status {
  border-color: #3b82f6;
}

.event-status .timeline-dot {
  border-color: #3b82f6;
}

.event-step {
  border-color: #8b5cf6;
}

.event-step .timeline-dot {
  border-color: #8b5cf6;
}

.event-sent {
  border-color: #f59e0b;
}

.event-sent .timeline-dot {
  border-color: #f59e0b;
}

/* Status Classes */
.status-new {
  background: #dbeafe;
  color: #1e40af;
}

.status-opened {
  background: #dcfce7;
  color: #166534;
}

.status-cancelled {
  background: #fee2e2;
  color: #991b1b;
}

.status-completed {
  background: #d1fae5;
  color: #065f46;
}

/* Animations */
.slide-fade-enter-active {
  transition: all 0.2s ease;
}

.slide-fade-leave-active {
  transition: all 0.2s ease;
}

.slide-fade-enter,
.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}

/* Responsive */
@media (max-width: 768px) {
  .request-stations-container {
    padding: 8px;
  }

  .user-header {
    padding: 8px 10px;
  }

  .event-card {
    padding: 8px 10px;
  }

  .timeline-item {
    padding-right: 24px;
  }

  .user-avatar {
    width: 32px;
    height: 32px;
    font-size: 12px;
  }

  .user-name {
    font-size: 13px;
  }
}
</style>
